import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Divider from '@mui/material/Divider';

import { BreadcrumbHeader, StatusBadge, EmptyState } from '../../components/shared';
import { purchaseOrders, supplierPayments, partOrders } from '../../services/dataClient';
import '../../styles/design-tokens.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Table style constants ────────────────────────────────────────────────────
const TH = {
  backgroundColor: 'var(--secondary-light)',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'var(--grey-600)',
  textTransform: 'none',
  letterSpacing: 'normal',
  px: 2,
  py: '10px',
  borderBottom: '1px solid var(--grey-200)',
  whiteSpace: 'nowrap',
};

const TD = {
  px: 2,
  py: 0,
  border: 'none',
  fontSize: '0.875rem',
  color: 'var(--grey-800)',
  height: 44,
};

// ─── Info card ────────────────────────────────────────────────────────────────
function InfoCard({ title, children }) {
  return (
    <Box sx={{ backgroundColor: '#ffffff', border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid var(--grey-200)' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--grey-900)' }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 2.5, py: 2 }}>{children}</Box>
    </Box>
  );
}

// ─── Info row ─────────────────────────────────────────────────────────────────
function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
      <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', flexShrink: 0, mr: 2 }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right' }}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)', fontWeight: 500 }}>{value || '—'}</Typography>
        ) : (
          value ?? <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Payment status badge ─────────────────────────────────────────────────────
function PaymentBadge({ status }) {
  const isPaid = status === 'Paid';
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '10px',
      backgroundColor: isPaid ? '#E8F5E9' : '#FFF8E1',
    }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: isPaid ? '#2E7D32' : '#E65100', lineHeight: 1 }}>
        {status}
      </Typography>
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const po = purchaseOrders.find((p) => p.id === id);

  // Build line items array — support both legacy single-part and new lineItems array
  const lineItems = po?.lineItems?.length > 0
    ? po.lineItems
    : po
    ? [{ partNumber: po.partNumber, partDescription: po.partDescription, quantity: po.quantity, unitCost: po.unitCost }]
    : [];

  // Linked order lookup
  const linkedPartOrder = po?.linkedOrderId
    ? partOrders.find((o) => o.id === po.linkedOrderId)
    : null;

  // Payment info
  const payment = po ? supplierPayments.find((p) => p.poId === po.id) : null;

  // Always call hooks before any early return
  if (!po) {
    return (
      <Box sx={{ p: 4 }}>
        <EmptyState title="Purchase order not found" description="This PO may have been deleted or the link is incorrect." />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography
            onClick={() => navigate('/accounting/purchase-orders')}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            ← Back to Purchase Orders
          </Typography>
        </Box>
      </Box>
    );
  }

  // Computed totals
  const computedTotal = lineItems.reduce((acc, li) => acc + ((li.unitCost ?? 0) * (li.quantity ?? 0)), 0);

  // Linked order display
  const linkedOrderNumber = po.linkedTicketNumber ?? null;
  const linkedOrderIsRep  = linkedOrderNumber?.startsWith('REP');
  const linkedOrderHref   = linkedOrderIsRep
    ? `/orders/part-replenishment/${po.linkedOrderId}`
    : `/orders/parts/${po.linkedOrderId}`;

  return (
    <Box>
      {/* Page heading */}
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <BreadcrumbHeader
          title={po.poNumber}
          subtitle={`${po.vendorName} · Created ${fmtDate(po.date)}`}
          crumbs={[
            { label: 'Accounting' },
            { label: 'Purchase Orders', path: '/accounting/purchase-orders' },
            { label: po.poNumber },
          ]}
        />
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, pb: 4, display: 'flex', gap: 3, alignItems: 'flex-start' }}>

        {/* ── Left column ── */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Line Items */}
          <Box sx={{ backgroundColor: '#ffffff', border: '1px solid var(--grey-200)', borderRadius: '8px', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
                Line Items ({lineItems.length})
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--grey-900)' }}>
                {fmt(computedTotal || po.totalCost)}
              </Typography>
            </Box>
            <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Part #', 'Description', 'Qty', 'Unit Cost', 'Total'].map((h) => (
                      <TableCell key={h} sx={TH}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((li, idx) => {
                    const rowTotal = (li.unitCost ?? 0) * (li.quantity ?? 0);
                    return (
                      <TableRow key={idx} sx={{ borderBottom: idx < lineItems.length - 1 ? '1px solid var(--grey-200)' : 'none' }}>
                        <TableCell sx={{ ...TD, fontWeight: 500 }}>{li.partNumber}</TableCell>
                        <TableCell sx={TD}>{li.partDescription ?? '—'}</TableCell>
                        <TableCell sx={TD}>{li.quantity ?? '—'}</TableCell>
                        <TableCell sx={TD}>{li.unitCost != null ? fmt(li.unitCost) : '—'}</TableCell>
                        <TableCell sx={{ ...TD, fontWeight: 600 }}>{rowTotal > 0 ? fmt(rowTotal) : '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>

          {/* Notes */}
          {po.notes && (
            <InfoCard title="Notes">
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', lineHeight: 1.6 }}>
                {po.notes}
              </Typography>
            </InfoCard>
          )}
        </Box>

        {/* ── Right column ── */}
        <Box sx={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* PO Info */}
          <InfoCard title="PO Details">
            <InfoRow label="PO Number"   value={po.poNumber} />
            <InfoRow label="Supplier"    value={po.vendorName} />
            <InfoRow label="Status"      value={<StatusBadge status={po.status} />} />
            <InfoRow label="Date Issued" value={fmtDate(po.date)} />
            {po.expectedDeliveryDate && (
              <InfoRow label="Expected Delivery" value={fmtDate(po.expectedDeliveryDate)} />
            )}
          </InfoCard>

          {/* Linked Order */}
          <InfoCard title="Linked Order">
            {linkedOrderNumber ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
                  {linkedOrderIsRep ? 'Replenishment Order' : 'Part Order'}:
                </Typography>
                <Typography
                  onClick={() => navigate(linkedOrderHref)}
                  sx={{ fontSize: '0.875rem', color: '#009688', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  {linkedOrderNumber}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>
                No linked order
              </Typography>
            )}
            {linkedPartOrder && (
              <Box sx={{ mt: 1.5 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Ticket</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-800)', fontWeight: 500 }}>{linkedPartOrder.ticketNumber}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Customer</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-800)' }}>{linkedPartOrder.customerName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Status</Typography>
                    <StatusBadge status={linkedPartOrder.status} />
                  </Box>
                </Box>
              </Box>
            )}
          </InfoCard>

          {/* Payment */}
          <InfoCard title="Payment">
            {payment ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Payment #</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)', fontWeight: 500 }}>{payment.paymentNumber}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Amount</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--grey-900)' }}>{fmt(payment.amount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Status</Typography>
                  <PaymentBadge status={payment.status} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Due Date</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{fmtDate(payment.dueDate)}</Typography>
                </Box>
                {payment.paidDate && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Paid On</Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 500 }}>{fmtDate(payment.paidDate)}</Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>
                No payment record
              </Typography>
            )}
          </InfoCard>

        </Box>
      </Box>
    </Box>
  );
}
